// src/components/alerts/FlowSenseAlertAiPanel.js
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (Alert System AI) — NEW: FlowSense AI alert monitor panel
//   • Health score (0–100) with the reasons it is not 100.
//   • Insights the server-side monitor found: alerts waiting too long, alert storm, incidents
//     (several alerts on one asset / a burst across sensors), noisy and flapping rules (one-click
//     "apply suggested duration"), monitored sensors that went silent.
//   • Priority queue: open alerts ranked by the explainable AI triage score (hover for reasons).
//   • Ask FlowSense AI (Ollama) about the current alert picture — the question goes to the
//     existing /flowsense-ai/ask endpoint with a compact snapshot of this panel as context.
//   Updates live: the monitor pushes 'alert_ai_snapshot' over the WebSocket.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Badge, Box, Button, CircularProgress, CircularProgressLabel, Collapse, Divider, Flex, HStack, Icon, IconButton,
    Input, InputGroup, InputRightElement, Skeleton, Spinner, Stack, Text, Tooltip, VStack, Wrap, WrapItem,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    MdAutoAwesome, MdRefresh, MdSend, MdHourglassTop, MdThunderstorm, MdHub, MdTune, MdSensorsOff, MdCheckCircle,
    MdNotificationsActive, MdExpandMore, MdExpandLess,
} from 'react-icons/md';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { fmtMinutes, healthColor, scoreColor, severityColor, statusLabel, timeAgo } from './alertUtils';

const KIND_ICON = { stale: MdHourglassTop, storm: MdThunderstorm, incident: MdHub, flapping: MdTune, noisy: MdTune, offline: MdSensorsOff, ok: MdCheckCircle };
const QUICK_QUESTIONS = [
    { label: 'What first?', q: 'What should we handle first, and why?' },
    { label: 'Supervisor summary', q: 'Summarize the alert situation for a supervisor in 4 bullet points.' },
    { label: 'Noisy rules', q: 'Which rules are creating noise and how should we tune them?' },
];

/** Compact, factual snapshot for the LLM (≤ ~4 KB). */
const buildAiContext = (intel) => {
    if (!intel) return {};
    return {
        module: 'Alert System',
        health: intel.health ? { score: intel.health.score, label: intel.health.label, deductions: intel.health.deductions } : null,
        open_by_severity: intel.stats?.alerts?.openBySeverity,
        kpis: intel.kpis,
        storm: intel.storm,
        top_priority_alerts: (intel.triage || []).slice(0, 10).map(t => ({
            id: t.id, severity: t.severity, status: t.status, sensor: t.sensor_name || t.sensor_id, asset: t.asset_location || t.asset_id,
            rule: t.rule_name, message: String(t.alert_message || '').slice(0, 140), open_for_min: t.age_min, ai_priority: t.score, reasons: t.reasons,
        })),
        incidents: (intel.incidents || []).slice(0, 5).map(i => ({ title: i.title, severity: i.severity, sensors: i.sensors.slice(0, 6), alerts: i.alert_ids.length })),
        rule_issues: Object.values(intel.rule_health || {}).slice(0, 5).map(r => ({ rule: r.name, label: r.label, alerts_7d: r.alerts7d, auto_resolved_pct: r.autoResolvedPct, advice: r.advice })),
        silent_sensors: (intel.silent_sensors || []).slice(0, 10).map(s => s.sensor_name || s.sensor_id),
        insights: (intel.insights || []).map(i => i.title),
    };
};

const InsightCard = ({ insight, onFocusAlerts, onFilter, onApplyRuleTuning, onOpenRule }) => {
    const [busy, setBusy] = useState(false);
    const border = useColorModeValue('gray.100', 'gray.700');
    const bg = useColorModeValue('white', 'gray.800');
    const color = insight.kind === 'ok' ? 'green' : severityColor(insight.severity);
    const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
    const act = insight.action || {};
    return (
        <Box p={3} borderRadius="xl" borderWidth="1px" borderColor={border} bg={bg} borderLeftWidth="4px" borderLeftColor={`${color}.400`}>
            <HStack align="flex-start" spacing={3}>
                <Box p={1.5} bg={iconBg} borderRadius="md"><Icon as={KIND_ICON[insight.kind] || MdAutoAwesome} color={`${color}.400`} boxSize={4} /></Box>
                <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="700" lineHeight="1.3">{insight.title}</Text>
                    {insight.detail && <Text fontSize="xs" color="gray.500" mt={1}>{insight.detail}</Text>}
                    <HStack mt={2} spacing={2} flexWrap="wrap">
                        {insight.alert_ids?.length > 0 && (
                            <Button size="xs" variant="outline" onClick={() => onFocusAlerts(insight.alert_ids)}>Show {insight.alert_ids.length} alert{insight.alert_ids.length > 1 ? 's' : ''}</Button>
                        )}
                        {act.type === 'filter' && <Button size="xs" variant="ghost" onClick={() => onFilter(act.status)}>Filter: {statusLabel(act.status)}</Button>}
                        {act.type === 'tune_rule' && (
                            <Button size="xs" colorScheme="purple" isLoading={busy} leftIcon={<MdTune />}
                                onClick={async () => { setBusy(true); try { await onApplyRuleTuning(act.rule_id, act.duration_minutes); } finally { setBusy(false); } }}>
                                Set duration to {act.duration_minutes} min
                            </Button>
                        )}
                        {act.type === 'open_rule' && onOpenRule && <Button size="xs" variant="ghost" onClick={() => onOpenRule(act.rule_id)}>Open rule</Button>}
                    </HStack>
                </Box>
            </HStack>
        </Box>
    );
};

function FlowSenseAlertAiPanel({ intel, loading, error, onRefresh, onFocusAlerts, onFilter, onOpenAlert, onApplyRuleTuning, onOpenRule, isConnected }) {
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState(null);
    const [asking, setAsking] = useState(false);
    const [askError, setAskError] = useState(null);
    const [showAllInsights, setShowAllInsights] = useState(false);

    const panelBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('purple.100', 'purple.800');
    const headerBg = useColorModeValue('linear(to-r, purple.50, blue.50)', 'linear(to-r, purple.900, blue.900)');
    const answerBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const rowHover = useColorModeValue('gray.50', 'whiteAlpha.100');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const health = intel?.health;
    const insights = useMemo(() => intel?.insights || [], [intel]);
    const shownInsights = showAllInsights ? insights : insights.slice(0, 4);
    const queue = useMemo(() => (intel?.triage || []).slice(0, 6), [intel]);

    const ask = useCallback(async (q) => {
        const text = String(q ?? question).trim();
        if (!text) return;
        setAsking(true);
        setAskError(null);
        setAnswer(null);
        try {
            const res = await askFlowSenseAi(text, buildAiContext(intel), 'alerts');
            setAnswer({ q: text, text: res?.answer || '', model: res?.model, truncated: res?.truncated });
        } catch (e) {
            setAskError(e?.message || 'FlowSense AI is unavailable right now — the analysis above is still current.');
        } finally {
            setAsking(false);
        }
    }, [question, intel]);

    return (
        <Box bg={panelBg} borderWidth="2px" borderColor={border} borderRadius="2xl" overflow="hidden" boxShadow="md">
            <Flex bgGradient={headerBg} px={4} py={3} align="center" justify="space-between">
                <HStack spacing={2}>
                    <Icon as={MdAutoAwesome} color="purple.400" boxSize={5} />
                    <Box>
                        <Text fontWeight="800" fontSize="md" lineHeight="1.2">FlowSense AI · Alert monitor</Text>
                        <Text fontSize="xs" color={muted}>
                            {isConnected ? 'Watching live' : 'Live updates paused'}
                            {intel?.generated_at ? ` · analysed ${timeAgo(intel.generated_at)}` : ''}
                        </Text>
                    </Box>
                </HStack>
                <Tooltip hasArrow label="Re-run the analysis now">
                    <IconButton aria-label="Re-run FlowSense AI analysis" size="sm" variant="ghost" icon={<MdRefresh />} onClick={onRefresh} isLoading={loading} />
                </Tooltip>
            </Flex>

            <Stack spacing={4} p={4}>
                {/* health */}
                {!intel && loading ? <Skeleton h="72px" borderRadius="xl" /> : error && !intel ? (
                    <Text fontSize="sm" color="orange.400">{error}</Text>
                ) : health ? (
                    <HStack spacing={4} align="center">
                        <CircularProgress value={health.score} color={`${healthColor(health.score)}.400`} size="72px" thickness="10px">
                            <CircularProgressLabel fontWeight="800" fontSize="lg">{health.score}</CircularProgressLabel>
                        </CircularProgress>
                        <Box flex="1" minW={0}>
                            <HStack><Text fontWeight="800">Alert health</Text><Badge colorScheme={healthColor(health.score)}>{health.label}</Badge></HStack>
                            {health.deductions?.length ? (
                                <Wrap spacing={1} mt={1}>
                                    {health.deductions.map(d => (
                                        <WrapItem key={d.why}><Badge variant="subtle" colorScheme="gray" fontSize="10px" textTransform="none">−{d.pts} {d.why}</Badge></WrapItem>
                                    ))}
                                </Wrap>
                            ) : <Text fontSize="xs" color={muted} mt={1}>Nothing is pulling the score down.</Text>}
                        </Box>
                    </HStack>
                ) : null}

                {/* insights */}
                {insights.length > 0 && (
                    <Box>
                        <Text fontSize="xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wide" mb={2}>What FlowSense AI found</Text>
                        <VStack align="stretch" spacing={2}>
                            {shownInsights.map(i => (
                                <InsightCard key={i.id} insight={i} onFocusAlerts={onFocusAlerts} onFilter={onFilter} onApplyRuleTuning={onApplyRuleTuning} onOpenRule={onOpenRule} />
                            ))}
                        </VStack>
                        {insights.length > 4 && (
                            <Button size="xs" variant="ghost" mt={1} rightIcon={showAllInsights ? <MdExpandLess /> : <MdExpandMore />} onClick={() => setShowAllInsights(v => !v)}>
                                {showAllInsights ? 'Show fewer' : `Show all ${insights.length}`}
                            </Button>
                        )}
                    </Box>
                )}

                {/* priority queue */}
                {queue.length > 0 && (
                    <Box>
                        <Text fontSize="xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wide" mb={2}>Priority queue · AI triage</Text>
                        <VStack align="stretch" spacing={1}>
                            {queue.map(t => (
                                <Tooltip key={t.id} hasArrow placement="left" label={t.reasons?.join(' · ')}>
                                    <HStack as="button" type="button" onClick={() => onOpenAlert(t.id)} textAlign="left" p={2} borderRadius="lg" _hover={{ bg: rowHover }} spacing={3} w="100%">
                                        <Badge colorScheme={scoreColor(t.score)} minW="34px" textAlign="center" borderRadius="md" fontSize="sm">{t.score}</Badge>
                                        <Box flex="1" minW={0}>
                                            <Text fontSize="sm" fontWeight="700" noOfLines={1}>#{t.id} · {t.sensor_name || t.sensor_id}</Text>
                                            <Text fontSize="xs" color={muted} noOfLines={1}>{t.alert_message}</Text>
                                        </Box>
                                        <VStack spacing={0} align="flex-end">
                                            <Badge colorScheme={severityColor(t.severity)} fontSize="10px">{t.severity}</Badge>
                                            <Text fontSize="10px" color={muted}>{fmtMinutes(t.age_min)}</Text>
                                        </VStack>
                                    </HStack>
                                </Tooltip>
                            ))}
                        </VStack>
                    </Box>
                )}
                {intel && !queue.length && (
                    <HStack color="green.400" fontSize="sm"><Icon as={MdNotificationsActive} /><Text>No open alerts to triage.</Text></HStack>
                )}

                <Divider />

                {/* ask */}
                <Box>
                    <Text fontSize="xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wide" mb={2}>Ask FlowSense AI</Text>
                    <Wrap spacing={1} mb={2}>
                        {QUICK_QUESTIONS.map(({ label, q }) => (
                            <WrapItem key={label}><Button size="xs" variant="outline" colorScheme="purple" onClick={() => { setQuestion(q); ask(q); }} isDisabled={asking}>{label}</Button></WrapItem>
                        ))}
                    </Wrap>
                    <InputGroup size="sm">
                        <Input placeholder="e.g. Is the pressure drop on Pump 1 related to the other alerts?" value={question}
                            onChange={e => setQuestion(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') ask(); }} borderRadius="lg" maxLength={500} />
                        <InputRightElement>
                            {asking ? <Spinner size="xs" color="purple.400" /> : <IconButton aria-label="Ask" size="xs" variant="ghost" icon={<MdSend />} onClick={() => ask()} />}
                        </InputRightElement>
                    </InputGroup>
                    <Collapse in={!!answer || !!askError || asking} animateOpacity>
                        <Box mt={3} p={3} bg={answerBg} borderRadius="lg" fontSize="sm">
                            {asking && <Text color={muted}>FlowSense AI is reading the alert picture…</Text>}
                            {askError && <Text color="orange.400">{askError}</Text>}
                            {answer && (
                                <>
                                    <Text fontSize="xs" color={muted} mb={1}>Q: {answer.q}</Text>
                                    <Text whiteSpace="pre-wrap">{answer.text}</Text>
                                    {answer.model && <Badge mt={2} colorScheme="purple" variant="subtle" fontSize="10px">FlowSense AI · {answer.model}</Badge>}
                                </>
                            )}
                        </Box>
                    </Collapse>
                </Box>
            </Stack>
        </Box>
    );
}

FlowSenseAlertAiPanel.propTypes = {
    intel: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRefresh: PropTypes.func.isRequired,
    onFocusAlerts: PropTypes.func.isRequired,
    onFilter: PropTypes.func.isRequired,
    onOpenAlert: PropTypes.func.isRequired,
    onApplyRuleTuning: PropTypes.func.isRequired,
    onOpenRule: PropTypes.func,
    isConnected: PropTypes.bool,
};

export { buildAiContext };
export default React.memo(FlowSenseAlertAiPanel);
// --- END UPDATE
